import { useTranslate, useGo, useCustomMutation } from "@refinedev/core";
import React, { useEffect, useState } from "react";
import { Form, Input } from "antd";
import styled from "styled-components";
import { useBoundStore } from "@/store";
import { LockIcon } from "@phosphor-icons/react";
import { PASSWORD_COMPLEXITY_PATTERN } from "constants/auth";
import { confirmPasswordRules } from "helpers/inputValidation/confirmPasswordValidation";
import { AuthPage, AuthRightPanel } from "./components/AuthLayout";
import { CoverflowCarousel } from "./components/CoverflowCarousel";
import { AuthFormPanel } from "./components/AuthFormPanel";
import {
  AuthInput,
  AuthSubmitButton,
  AuthLink,
  AuthSecurityNote,
} from "./components/AuthFormElements";

const ForgotPasswordPage: React.FC = () => {
  const t = useTranslate();
  const [email, setEmail] = useState("");
  const [step, setStep] = useState<"forgot" | "reset">("forgot");
  const go = useGo();
  const setSidebarCollapsed = useBoundStore(
    (state) => state.setSidebarCollapsed
  );

  useEffect(() => {
    setSidebarCollapsed(true);
    return () => {
      setSidebarCollapsed(false);
    };
  }, [setSidebarCollapsed]);

  const { mutate: forgotPasswordMutate, isPending: forgotPasswordLoading } =
    useCustomMutation();

  const {
    mutate: confirmForgotPasswordMutate,
    isPending: resetPasswordLoading,
  } = useCustomMutation();

  const handleForgotPassword = (values: { email: string }) => {
    forgotPasswordMutate(
      {
        url: "/api/users/forgot_password",
        method: "post",
        values: values,
      },
      {
        onSuccess: () => {
          setEmail(values.email);
          setStep("reset");
        },
      }
    );
  };

  const handleConfirmForgotPassword = (values: {
    code: string;
    password: string;
    confirmPassword: string;
  }) => {
    confirmForgotPasswordMutate(
      {
        url: "/api/users/confirm_forgot_password",
        method: "post",
        values: {
          email,
          confirm_code: values.code,
          password: values.password,
        },
        successNotification: {
          type: "success",
          message: t("pages.auth.forgot-password.passwordReset"),
        },
      },
      {
        onSuccess: () => {
          go({ to: "/login" });
        },
      }
    );
  };

  const title =
    step === "forgot"
      ? t("pages.auth.forgot-password.title")
      : t("pages.auth.forgot-password.resetTitle");

  return (
    <AuthPage>
      <CoverflowCarousel />
      <AuthRightPanel>
        <AuthFormPanel title={title}>
          {step === "forgot" && (
            <>
              <Subtitle>
                {t("pages.auth.forgot-password.verifyIdentity")}
              </Subtitle>

              <Form
                name="forgot-password"
                onFinish={handleForgotPassword}
                layout="vertical"
                requiredMark={false}
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <Form.Item
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: t("pages.auth.new-register.pleaseEnterYour"),
                    },
                    {
                      type: "email",
                      message: t("pages.auth.new-register.pleaseEnterA"),
                    },
                  ]}
                  style={{ margin: 0 }}
                >
                  <AuthInput
                    type="email"
                    placeholder={t(
                      "pages.auth.forgot-password.emailPlaceholder"
                    )}
                    autoComplete="email"
                  />
                </Form.Item>

                <AuthSubmitButton
                  type="submit"
                  disabled={forgotPasswordLoading}
                >
                  {t("continue")}
                </AuthSubmitButton>
              </Form>

              <AuthLink
                onClick={() => go({ to: "/login" })}
                style={{
                  display: "block",
                  textAlign: "center",
                  marginTop: 16,
                  fontSize: 14,
                }}
              >
                ← {t("pages.auth.forgot-password.backToLogin")}
              </AuthLink>
            </>
          )}

          {step === "reset" && (
            <>
              <Subtitle>
                {t("pages.auth.forgot-password.enterCodeWithEmail", { email })}
              </Subtitle>

              <Form
                name="reset-password"
                onFinish={handleConfirmForgotPassword}
                layout="vertical"
                requiredMark={false}
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <OTPWrapper>
                  <Form.Item
                    name="code"
                    rules={[
                      {
                        required: true,
                        message: t(
                          "pages.auth.forgot-password.pleaseEnterCode"
                        ),
                      },
                    ]}
                    style={{ margin: 0 }}
                  >
                    <Input.OTP size="large" length={6} />
                  </Form.Item>
                </OTPWrapper>

                <Form.Item
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: t("pages.auth.forgot-password.pleaseEnterCode"),
                    },
                    {
                      min: 12,
                      message: t("pages.auth.new-register.passwordMustBe"),
                    },
                    {
                      pattern: PASSWORD_COMPLEXITY_PATTERN,
                      message: t("pages.auth.new-register.passwordMustContain"),
                    },
                  ]}
                  style={{ margin: 0 }}
                >
                  <AuthInput
                    type="password"
                    placeholder={t(
                      "pages.auth.forgot-password.passwordPlaceholder"
                    )}
                    autoComplete="new-password"
                  />
                </Form.Item>

                <Form.Item
                  name="confirmPassword"
                  dependencies={["password"]}
                  rules={confirmPasswordRules(t, "password")}
                  style={{ margin: 0 }}
                >
                  <AuthInput
                    type="password"
                    placeholder={t(
                      "pages.auth.forgot-password.passwordPlaceholder"
                    )}
                    autoComplete="new-password"
                  />
                </Form.Item>

                <AuthSubmitButton type="submit" disabled={resetPasswordLoading}>
                  {t("continue")}
                </AuthSubmitButton>
              </Form>

              <AuthLink
                onClick={() => go({ to: "/login" })}
                style={{
                  display: "block",
                  textAlign: "center",
                  marginTop: 16,
                  fontSize: 14,
                }}
              >
                ← {t("pages.auth.forgot-password.backToLogin")}
              </AuthLink>
            </>
          )}

          <AuthSecurityNote>
            <LockIcon size={12} />
            {t("pages.auth.new-register.trustBadge")}
          </AuthSecurityNote>
        </AuthFormPanel>
      </AuthRightPanel>
    </AuthPage>
  );
};

export default ForgotPasswordPage;

const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  text-align: center;
  margin: 0;
  line-height: 1.5;
`;

const OTPWrapper = styled.div`
  .ant-otp {
    width: 100%;
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }

  .ant-otp-input {
    flex: 1;
    height: 56px;
    font-size: 24px;
    font-weight: 600;
    font-family: "Inter Variable", sans-serif;
    border-radius: 8px;
    border-color: #e5e7eb;
    transition:
      border-color 0.15s,
      box-shadow 0.15s;

    &:focus {
      border-color: #14b8a6;
      box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.1);
    }
  }
`;
